import {
  Badge,
  Card,
  CardDescription,
  CardTitle,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  Tooltip,
  TrafficLight,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';

import { useWorkspace } from '../workspace/WorkspaceContext';
import {
  useRetentionBreakdown,
  type PeriodArg,
  type RetentionBreakdownRow,
  type RetentionCurveSeries,
} from '../../lib/api';
import { centavosTexto, motivoSemTaxa, taxaTexto, type TaxaLike } from './retention-ui';

/** Uma taxa numa célula: número, ou "—" com o motivo no tooltip; amostra pequena marcada. */
export function CelulaDeTaxa({ taxa }: { taxa: TaxaLike }) {
  if (taxa.valor === null) {
    return (
      <Tooltip content={motivoSemTaxa(taxa) ?? 'Sem dados.'} side="bottom">
        <span tabIndex={0} className="nums text-muted-foreground">
          {taxaTexto(taxa)}
        </span>
      </Tooltip>
    );
  }
  return (
    <span className="nums inline-flex items-center gap-1">
      {taxaTexto(taxa)}
      {taxa.amostraPequena ? (
        <Tooltip
          content={`Pouca gente para tirar conclusão (${formatNumber(taxa.vencidos)}).`}
          side="bottom"
        >
          <span tabIndex={0} className="text-2xs text-warning" aria-label="amostra pequena">
            n={formatNumber(taxa.vencidos)}
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}

/**
 * A quebra da Curva de Evasão (por anúncio, origem, campanha da Meta…): o "CPL real de
 * verdade" do gestor de tráfego — ordenada pelo servidor por custo por quem ficou D7.
 */
export function TabelaDeQuebra({
  rows,
  caption,
  primeiraColuna,
  comCusto,
}: {
  rows: readonly RetentionBreakdownRow[];
  caption: string;
  primeiraColuna: string;
  comCusto: boolean;
}) {
  return (
    <Table caption={caption} density="dense">
      <TableHeader>
        <TableRow>
          <TableHead>{primeiraColuna}</TableHead>
          <TableHead numeric>Entradas</TableHead>
          <TableHead numeric>Evasão 1º dia</TableHead>
          <TableHead numeric>Evasão 7 dias</TableHead>
          <TableHead numeric>Evasão 30 dias</TableHead>
          <TableHead numeric>Você removeu (7d)</TableHead>
          {comCusto ? <TableHead numeric>Custo/ficou 7d</TableHead> : null}
          {comCusto ? <TableHead numeric>Custo/ficou 30d</TableHead> : null}
          <TableHead>Confiança</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableRowHeader>
              <span className="inline-flex flex-wrap items-center gap-1.5">
                {r.nome}
                <TrafficLight
                  status={r.semaforo}
                  compact
                  hint={
                    r.metaD1 !== null
                      ? `Evasão 1º dia contra a sua meta: até ${formatPercent(r.metaD1, 0)}`
                      : 'Evasão 1º dia contra a mediana de 28 dias do workspace'
                  }
                />
              </span>
            </TableRowHeader>
            <TableCell numeric>{formatNumber(r.entradas)}</TableCell>
            <TableCell numeric nowrap>
              <CelulaDeTaxa taxa={r.evasaoD1} />
            </TableCell>
            <TableCell numeric nowrap>
              <CelulaDeTaxa taxa={r.evasaoD7} />
            </TableCell>
            <TableCell numeric nowrap>
              <CelulaDeTaxa taxa={r.evasaoD30} />
            </TableCell>
            <TableCell numeric nowrap>
              <CelulaDeTaxa taxa={r.removidosD7} />
            </TableCell>
            {comCusto ? (
              <TableCell numeric nowrap>
                <CustoCelula centavos={r.custoPorFicouD7Centavos} gasto={r.gasto} />
              </TableCell>
            ) : null}
            {comCusto ? (
              <TableCell numeric nowrap>
                <CustoCelula centavos={r.custoPorFicouD30Centavos} gasto={r.gasto} />
              </TableCell>
            ) : null}
            <TableCell nowrap>
              {r.confianca === null ? (
                '—'
              ) : (
                <Badge tone="neutral" dot>
                  {r.confianca.deterministico > 0
                    ? `${formatPercent(r.confianca.deterministico, 0)} com prova`
                    : 'Provável'}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CustoCelula({
  centavos,
  gasto,
}: {
  centavos: number | null;
  gasto: RetentionBreakdownRow['gasto'];
}) {
  if (centavos !== null)
    return <span className="nums">{centavosTexto(centavos, gasto.moeda)}</span>;
  const motivo =
    gasto.state === 'UNKNOWN'
      ? 'Sem gasto da Meta para este recorte (sem vínculo ou sincronização parada) — não é "não gastou".'
      : gasto.state === 'NO_SPEND'
        ? 'A Meta não registrou gasto neste período.'
        : 'Nenhuma turma vencida, ou ninguém ficou.';
  return (
    <Tooltip content={motivo} side="bottom">
      <span tabIndex={0} className="nums text-muted-foreground">
        —
      </span>
    </Tooltip>
  );
}

/** Evasão por origem (Histórico): Meta × link marcado × sem origem × sem atribuição. */
export function EvasaoPorOrigem({ periodo }: { periodo: PeriodArg }) {
  const { workspace } = useWorkspace();
  const q = useRetentionBreakdown(workspace.id, periodo, 'ORIGIN');
  if (q.isError) {
    return (
      <ErrorState
        title="Não foi possível carregar a evasão por origem"
        description={q.error.message}
        onRetry={() => void q.refetch()}
      />
    );
  }
  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>Evasão por origem</CardTitle>
      <CardDescription>
        De onde veio quem sai. &ldquo;Sem atribuição&rdquo; é entrada sem clique casado — origem
        desconhecida, não orgânico.
      </CardDescription>
      <div className="mt-3">
        {q.isPending ? (
          <TableSkeleton rows={4} columns={6} />
        ) : q.data.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma entrada registrada no período.</p>
        ) : (
          <TabelaDeQuebra
            rows={q.data.rows}
            caption="Evasão por origem"
            primeiraColuna="Origem"
            comCusto={false}
          />
        )}
      </div>
    </Card>
  );
}

/** A tabela por marco de UMA série: evasão / removidos / sem causa / ficaram (fecham 100%). */
export function TabelaPorMarco({ serie }: { serie: RetentionCurveSeries }) {
  return (
    <Table caption={`Marcos de ${serie.rotulo}`} density="dense">
      <TableHeader>
        <TableRow>
          <TableHead>Marco</TableHead>
          <TableHead numeric>Saiu sozinho</TableHead>
          <TableHead numeric>Você removeu</TableHead>
          <TableHead numeric>Sem causa</TableHead>
          <TableHead numeric>Ficaram</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {serie.pontos.map((p) => (
          <TableRow key={p.marco}>
            <TableRowHeader className="nums">D{p.marco}</TableRowHeader>
            <TableCell numeric>
              <CelulaDeTaxa taxa={p.evasao} />
            </TableCell>
            <TableCell numeric>
              <CelulaDeTaxa taxa={p.removidos} />
            </TableCell>
            <TableCell numeric>
              <CelulaDeTaxa taxa={p.semCausa} />
            </TableCell>
            <TableCell numeric>
              <CelulaDeTaxa taxa={p.ficaram} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
